import { CONSTANTS } from "@/constants";
import { DEFAULT_SEARCH_STATE } from "@/contexts/SearchContext/constants";
import { PropertyListingSearchReferrer } from "@/contexts/SearchContext/enums";
import { SearchState } from "@/contexts/SearchContext/interfaces";
import { Locale } from "@/enums/common.enum";
import {
  ListingType,
  PropertyAmenity,
  PropertyBadgeType,
  PropertyBathroomType,
  PropertyBedroomType,
  PropertyCompletionStatusType,
  PropertyFurnishingType,
  PropertyListingType,
  PropertyListingViewType,
  PropertyPurposeType,
  PropertyRentalPeriodType,
  PropertySorting,
  PropertyTourType,
  PropertyType,
} from "@/enums/property.enum";
import { getLocationsBySlugs } from "@/hooks/useProperty";
import { PropertySearchPageUrlSearchParams } from "@/interfaces/api.interface";
import {
  FetchPropertiesRequest,
  Location,
} from "@/interfaces/property.interface";
import { convertSearchParamsToObject } from "../../utils";

/*
 Parses the URL and provides with the corresponding state
*/
export const convertUrlToState = ({
  searchParams,
  locations,
}: {
  searchParams: PropertySearchPageUrlSearchParams;
  locations: Location[];
}) => {
  if (searchParams instanceof URLSearchParams) {
    searchParams = convertSearchParamsToObject(searchParams);
  }

  const newState: Partial<SearchState> = {
    locations: [],
  };

  newState.listing = {
    type: searchParams.listing_type as PropertyListingType,
    property: searchParams.property_type
      ? (searchParams.property_type as PropertyType)
      : null,
  };

  if (searchParams.locations) {
    newState.locations =
      locations?.map((location) => ({
        ...location,
        label: location.name,
        value: location.fullSlug.toString(),
      })) || [];
  }

  newState.price = {
    from: searchParams.price_from ? Number(searchParams.price_from) : null,
    to: searchParams.price_to ? Number(searchParams.price_to) : null,
  };

  newState.area = {
    from: searchParams.area_from ? Number(searchParams.area_from) : null,
    to: searchParams.area_to ? Number(searchParams.area_to) : null,
  };

  newState.bedrooms =
    (searchParams.beds?.split(",") as PropertyBedroomType[]) ?? null;

  const baths = searchParams.baths?.split(",");
  newState.bathrooms = baths
    ? baths.map((bath) => bath as PropertyBathroomType)
    : null;

  const amenities = searchParams.amenities?.split(",");
  newState.amenities = amenities
    ? amenities.map((amenity) => amenity as PropertyAmenity)
    : null;

  newState.rentalPeriod =
    searchParams.rental_period as PropertyRentalPeriodType;

  newState.purpose =
    (searchParams.purpose as PropertyPurposeType) ??
    PropertyPurposeType.RESIDENTIAL;

  if (
    newState.listing.type === PropertyListingType.COMMERCIAL_BUY ||
    newState.listing.type === PropertyListingType.COMMERCIAL_RENT
  ) {
    newState.purpose = PropertyPurposeType.COMMERCIAL;
  }

  newState.sort =
    (searchParams.sort as PropertySorting) || PropertySorting.FEATURED;

  newState.pageParams = {
    page: Number(searchParams.page) || 1,
    size: Number(searchParams.size) || 20,
  };
  newState.view =
    (searchParams.view as PropertyListingViewType) ||
    PropertyListingViewType.LIST;

  newState.furnishing =
    (searchParams.furnishing as PropertyFurnishingType) ?? null;
  newState.badges = searchParams.badges
    ? (searchParams.badges?.split(",") as Array<PropertyBadgeType>)
    : null;

  if (searchParams.coordinates) {
    newState.coordinates = searchParams.coordinates?.split(",").map(Number) as [
      number,
      number,
      number,
      number
    ];
  }

  newState.referrer =
    (searchParams.referrer as PropertyListingSearchReferrer) ?? null;

  newState.tour = (searchParams.tour as PropertyTourType) ?? null;
  newState.agencyId = searchParams.agency_id ?? null;

  return newState;
};

/*
Transforms search state into the body required by the GET properties API
*/
export const convertStateToPropertyFilters = (searchState: SearchState) => {
  const {
    bathrooms,
    bedrooms,
    price,
    area,
    amenities,
    pageParams,
    coordinates,
    view,
    locations,
    rentalPeriod,
    furnishing,
    listing,
    purpose,
    sort,
    tour,
    badges,
    agencyId,
  } = searchState;

  const filter: Partial<FetchPropertiesRequest> = {
    completionStatus: PropertyCompletionStatusType.READY,
  };

  if (locations && locations?.length > 0) {
    filter["locations"] = locations.map((location) => location.fullSlug);
  }

  switch (listing.type) {
    case PropertyListingType.OFF_PLAN:
      filter["completionStatus"] = PropertyCompletionStatusType.OFF_PLAN;
      break;
    case PropertyListingType.COMMERCIAL_BUY:
      filter["listingType"] = ListingType.BUY;
      filter["propertyGenericType"] = PropertyPurposeType.COMMERCIAL;
      break;
    case PropertyListingType.COMMERCIAL_RENT:
      filter["listingType"] = ListingType.RENT;
      filter["propertyGenericType"] = PropertyPurposeType.COMMERCIAL;
      break;
    case PropertyListingType.BUY:
      filter["listingType"] = ListingType.BUY;
      break;
    case PropertyListingType.RENT:
      filter["listingType"] = ListingType.RENT;
      break;
  }

  if (purpose === PropertyPurposeType.RESIDENTIAL) {
    if (listing.type === PropertyListingType.RENT) {
      if (rentalPeriod) {
        filter["rentalPeriod"] = rentalPeriod;
      }
    }

    if (bathrooms) {
      filter["bathrooms"] = bathrooms.join(",");
    }

    if (bedrooms) {
      filter["bedrooms"] = bedrooms.join(",");
    }
  }

  if (price?.from) {
    filter["priceFrom"] = price.from;
  }

  if (price?.to) {
    filter["priceTo"] = price.to;
  }

  if (area?.from) {
    filter["areaFrom"] = area.from;
  }

  if (area?.to) {
    filter["areaTo"] = area.to;
  }

  if (amenities) {
    filter["amenities"] = amenities.join(",");
  }

  if (listing.type === PropertyListingType.OFF_PLAN) {
    filter["completionStatus"] = PropertyCompletionStatusType.OFF_PLAN;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (listing.property && (listing.property as any) !== "property") {
    filter["propertyType"] = listing.property;
  }

  if (sort !== PropertySorting.FEATURED) {
    filter["ordering"] = sort;
  }

  if (furnishing) {
    filter["furnishing"] = furnishing;
  }

  if (badges) {
    if (badges.includes(PropertyBadgeType.YALLA_VALUE_VERIFIED)) {
      filter["isFairPrice"] = true;
    }
  }

  filter["page"] = pageParams?.page;
  filter["limit"] = pageParams?.size;

  if (
    view === PropertyListingViewType.MAP &&
    coordinates &&
    (!filter["locations"] || filter["locations"]?.length === 0) &&
    coordinates.length === 4
  ) {
    // maximumLat, maximumLng, minimumLat, minimumLng
    filter["maxLatitude"] = coordinates[0];
    filter["maxLongitude"] = coordinates[1];
    filter["minLatitude"] = coordinates[2];
    filter["minLongitude"] = coordinates[3];
  }

  if (tour === PropertyTourType.FULL_360) {
    filter["view360Only"] = true;
  } else if (tour === PropertyTourType.VIDEO) {
    filter["videoTourOnly"] = true;
  }

  if (agencyId) {
    filter["agencyId"] = agencyId;
  }

  return filter as FetchPropertiesRequest;
};

export const convertPropertyFilterToState = async (
  filters: FetchPropertiesRequest,
  locale: string
) => {
  const state: Partial<SearchState> = {};

  state.listing = {
    property: null,
    type: PropertyListingType.BUY,
  };

  if (filters.propertyType) {
    state.listing.property = filters.propertyType;
  }

  if (filters.listingType) {
    if (
      filters.propertyGenericType === PropertyPurposeType.COMMERCIAL &&
      filters.listingType === ListingType.BUY
    ) {
      state.listing.type = PropertyListingType.COMMERCIAL_BUY;
    } else if (
      filters.propertyGenericType === PropertyPurposeType.COMMERCIAL &&
      filters.listingType === ListingType.RENT
    ) {
      state.listing.type = PropertyListingType.COMMERCIAL_RENT;
    } else if (
      filters.completionStatus === PropertyCompletionStatusType.OFF_PLAN
    ) {
      state.listing.type = PropertyListingType.OFF_PLAN;
    } else if (filters.listingType === ListingType.RENT) {
      state.listing.type = PropertyListingType.RENT;
    } else {
      state.listing.type = PropertyListingType.BUY;
    }
  }

  if (filters.amenities) {
    state.amenities = filters.amenities.split(",") as PropertyAmenity[];
  }

  if (filters.areaFrom) {
    state.area = {
      from: filters.areaFrom,
    };
  }

  if (filters.areaTo) {
    state.area = {
      to: filters.areaTo,
    };
  }

  if (filters.priceFrom) {
    state.price = {
      from: filters.priceFrom,
    };
  }

  if (filters.priceTo) {
    state.price = {
      to: filters.priceTo,
    };
  }

  if (filters.bathrooms) {
    state.bathrooms = filters.bathrooms.split(",") as PropertyBathroomType[];
  }

  if (filters.bedrooms) {
    state.bedrooms = filters.bedrooms.split(",") as PropertyBedroomType[];
  }

  if (filters.furnishing) {
    state.furnishing = filters.furnishing;
  }

  if (filters.locations) {
    const locations = await getLocationsBySlugs(filters.locations, locale);

    state.locations = locations.map((location) => ({
      ...location,
      label: location.name,
      value: location.slug,
    }));
  }

  if (filters.rentalPeriod) {
    state.rentalPeriod = filters.rentalPeriod;
  }

  if (filters.videoTourOnly) {
    state.tour = PropertyTourType.VIDEO;
  }

  if (filters.view360Only) {
    state.tour = PropertyTourType.FULL_360;
  }

  if (filters.ordering) {
    state.sort = filters.ordering as PropertySorting;
  }

  if (filters.isFairPrice) {
    state.badges = [PropertyBadgeType.YALLA_VALUE_VERIFIED];
  }
  return {
    ...DEFAULT_SEARCH_STATE,
    ...state,
  };
};

export const convertStateToListingUrl = (
  state: Partial<SearchState>,
  _updates: Partial<SearchState> = {}
) => {
  const updates = { ...state, ..._updates };

  const listingType = updates.listing?.type ?? PropertyListingType.BUY;
  const searchedLocations = updates.locations;
  let property = updates.listing?.property ?? null;

  let newURL = `/search`;

  const newParams = new URLSearchParams();

  // if listing type is changed the property must be reset
  if (listingType !== state.listing?.type) {
    property = null;
  }

  if (property) {
    newParams.set("property_type", property!);
  } else {
    newParams.delete("property_type");
  }

  const locationSlugs = searchedLocations?.map((location) => location.fullSlug);

  newParams.set("listing_type", listingType!);

  if (locationSlugs && locationSlugs.length > 0) {
    newParams.set("locations", locationSlugs?.join(","));
  } else {
    newParams.delete("locations");
  }

  if (updates.bathrooms && updates.bathrooms.length > 0) {
    newParams.set("baths", updates.bathrooms.join(","));
  } else {
    newParams.delete("baths");
  }

  if (updates.bedrooms && updates.bedrooms.length > 0) {
    newParams.set("beds", updates.bedrooms.join(","));
  } else {
    newParams.delete("beds");
  }

  if (updates.amenities !== undefined) {
    if (updates.amenities && updates.amenities.length > 0) {
      newParams.set("amenities", updates.amenities.join(","));
    } else {
      newParams.delete("amenities");
    }
  }

  if (updates.price) {
    if (updates.price.from && updates.price.from !== null) {
      newParams.set("price_from", updates.price.from.toString());
    } else {
      newParams.delete("price_from");
    }

    if (updates.price.to !== undefined) {
      if (updates.price.to !== null) {
        newParams.set("price_to", updates.price.to.toString());
      } else {
        newParams.delete("price_to");
      }
    }
  }

  if (updates.area) {
    if (updates.area.from !== undefined) {
      if (updates.area.from !== null) {
        newParams.set("area_from", updates.area.from.toString());
      } else {
        newParams.delete("area_from");
      }
    }
    if (updates.area.to !== undefined) {
      if (updates.area.to !== null) {
        newParams.set("area_to", updates.area.to.toString());
      } else {
        newParams.delete("area_to");
      }
    }
  }

  if (updates.rentalPeriod) {
    newParams.set("rental_period", updates.rentalPeriod);
  } else {
    newParams.delete("rental_period");
  }

  if (updates.tour) {
    newParams.set("tour", updates.tour);
  } else {
    newParams.delete("tour");
  }

  switch (updates.listing?.type) {
    case "buy":
    case "rent":
      newParams.set("purpose", PropertyPurposeType.RESIDENTIAL);
      break;
    case "commercial-buy":
    case "commercial-rent":
      newParams.set("purpose", PropertyPurposeType.COMMERCIAL);
      break;
    case "off-plan":
      // TODO: NOT SURE WHAT TO DO HERE
      break;
  }

  if (updates.sort !== undefined) {
    newParams.set("sort", updates.sort);
  } else {
    newParams.set("sort", "featured");
  }

  if (updates.pageParams && Object.keys(_updates).length === 1) {
    // ensuring user only has updated the pageParams
    newParams.set("page", updates.pageParams.page.toString());
    newParams.set("size", updates.pageParams.size.toString());
  } else {
    newParams.set("page", "1");
    newParams.set("size", "20");
  }

  if (updates.view) {
    newParams.set("view", updates.view);
  } else {
    newParams.set("view", "list");
  }

  if (updates.furnishing) {
    newParams.set("furnishing", updates.furnishing);
  } else {
    newParams.delete("furnishing");
  }

  if (updates.badges) {
    newParams.set("badges", updates.badges.join(","));
  } else {
    newParams.delete("badges");
  }

  if (updates.coordinates) {
    newParams.set("coordinates", updates.coordinates.join(","));
  }

  newURL += `?${newParams.toString()}`;
  return newURL;
};

export const makeUrlFromSlug = (locale: Locale, slug: string) => {
  if (locale === "en") {
    return `${CONSTANTS.APP_URL}/${locale}/${slug}`;
  }

  return `${CONSTANTS.APP_URL}/${locale}/${encodeURI(slug)}`;
};
